import React, { useState } from 'react';
import { Modal, Upload, Button, Checkbox, Form, Input, Space, Alert, Spin, App } from 'antd';
import { UploadOutlined, CheckOutlined } from '@ant-design/icons';
import { fileUpload } from '../../../../utils/storageUtils';
import { t } from '../../../../i18n';
import {
  getAvailableHubsFromZoneMappingFile,
  importZoneMappings,
  ZoneMappingImportResponse
} from '../graphql/mutations/zoneMappingMutations';

const { Dragger } = Upload;
const { TextArea } = Input;

interface ZoneMappingImportModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

const ZoneMappingImportModal: React.FC<ZoneMappingImportModalProps> = ({
  visible,
  onCancel,
  onSuccess
}) => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [uploading, setUploading] = useState(false);
  const [pickupGateFile, setPickupGateFile] = useState<File | null>(null);
  const [pickupGateFileObjectKey, setPickupGateFileObjectKey] = useState<string | null>(null);
  const [zoneMappingFile, setZoneMappingFile] = useState<File | null>(null);
  const [zoneMappingFileObjectKey, setZoneMappingFileObjectKey] = useState<string | null>(null);
  const [availableHubs, setAvailableHubs] = useState<string[]>([]);
  const [loadingHubs, setLoadingHubs] = useState(false);
  const [importResult, setImportResult] = useState<ZoneMappingImportResponse | null>(null);

  // 重置状态
  const resetState = () => {
    setPickupGateFile(null);
    setPickupGateFileObjectKey(null);
    setZoneMappingFile(null);
    setZoneMappingFileObjectKey(null);
    setAvailableHubs([]);
    setImportResult(null);
    form.resetFields();
  };

  const handleCancel = () => {
    resetState();
    onCancel();
  };

  // 处理Pickup Gate文件上传
  // beforeUpload 需要是 async 因为：
  // 1. 我们需要执行异步的文件上传操作 (await fileUpload)
  // 2. Ant Design 支持 beforeUpload 返回 Promise<boolean>
  // 
  // 返回值说明：
  // - 返回 false 或 Promise<false>: 阻止 Ant Design 的默认上传行为，使用自定义上传逻辑
  // - 返回 true 或 Promise<true>: 允许 Ant Design 使用默认上传（需要配置 action URL）
  // 
  // 在这个场景中，我们使用自定义上传逻辑（fileUpload），所以总是返回 false
  const handlePickupGateUpload = async (file: File): Promise<boolean> => {
    // 文件类型验证失败，阻止上传
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      message.error('只支持Excel文件（.xlsx, .xls）');
      return false;
    }

    setUploading(true);
    try {
      console.log('开始上传Pickup Gate文件:', file.name);
      const uploadResponse = await fileUpload(file, 'zone-mapping');
      setPickupGateFile(file);
      setPickupGateFileObjectKey(uploadResponse.objectKey);
      message.success('始发地Gate映射文件上传成功');
      // 使用自定义上传逻辑，返回 false 阻止默认上传
      return false;
    } catch (error: any) {
      console.error('Pickup Gate文件上传失败:', {
        error,
        errorMessage: error?.message,
        errorStack: error?.stack,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type
      });
      message.error(`文件上传失败：${error?.message || '未知错误'}`);
      // 即使失败也返回 false，阻止默认上传行为
      return false;
    } finally {
      setUploading(false);
    }
  };

  // 处理Zone Mapping文件上传
  // beforeUpload 需要是 async 因为：
  // 1. 我们需要执行异步的文件上传操作 (await fileUpload)
  // 2. 上传成功后还需要获取 HUB 列表 (await getAvailableHubsFromZoneMappingFile)
  // 3. Ant Design 支持 beforeUpload 返回 Promise<boolean>
  const handleZoneMappingUpload = async (file: File): Promise<boolean> => {
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      message.error('只支持Excel文件（.xlsx, .xls）');
      return false;
    }

    setUploading(true);
    setLoadingHubs(true);
    try {
      console.log('开始上传Zone Mapping文件:', file.name);
      const uploadResponse = await fileUpload(file, 'zone-mapping');
      setZoneMappingFile(file);
      setZoneMappingFileObjectKey(uploadResponse.objectKey);
      message.success('Zone映射文件上传成功');

      // 获取可用的HUB列
      const hubs = await getAvailableHubsFromZoneMappingFile(uploadResponse.objectKey);
      setAvailableHubs(hubs);
      
      if (hubs.length === 0) {
        message.warning('未找到可用的HUB列，请检查Excel文件格式');
      } else {
        message.info(`找到 ${hubs.length} 个可用的HUB列`);
      }
      return false; // 阻止默认上传行为
    } catch (error: any) {
      console.error('Zone Mapping文件上传失败:', {
        error,
        errorMessage: error?.message,
        errorStack: error?.stack,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type
      });
      message.error(`文件上传失败：${error?.message || '未知错误'}`);
      return false; // 即使失败也返回 false，阻止默认上传
    } finally {
      setUploading(false);
      setLoadingHubs(false);
    }
  };

  // 处理导入
  const handleImport = async () => {
    if (!pickupGateFileObjectKey || !zoneMappingFileObjectKey) {
      message.error('请先上传两个Excel文件');
      return;
    }

    const values = await form.validateFields();
    const selectedHubs = values.selectedHubs || [];
    
    if (selectedHubs.length === 0) {
      message.error('请至少选择一个HUB');
      return;
    }

    setUploading(true);
    try {
      // 构建Gateway映射（如果用户设置了）
      const gatewayMapping: Record<string, string> | undefined = values.gatewayMapping
        ? Object.fromEntries(
            values.gatewayMapping
              .split('\n')
              .filter((line: string) => line.trim())
              .map((line: string) => {
                const [hub, gateway] = line.split(':').map((s: string) => s.trim());
                return [hub, gateway];
              })
          )
        : undefined;

      const result = await importZoneMappings(
        pickupGateFileObjectKey,
        zoneMappingFileObjectKey,
        selectedHubs,
        gatewayMapping,
        values.reason
      );

      setImportResult(result);

      if (result.success) {
        message.success(`导入成功！成功 ${result.successCount} 条，失败 ${result.failedCount} 条`);
        if (result.failedCount === 0) {
          onSuccess();
        }
      } else {
        message.error(`导入失败：${result.message}`);
      }
    } catch (error: any) {
      message.error(`导入失败：${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const pickupGateUploadProps = {
    accept: '.xlsx,.xls',
    showUploadList: false,
    beforeUpload: handlePickupGateUpload,
    disabled: uploading
  };

  const zoneMappingUploadProps = {
    accept: '.xlsx,.xls',
    showUploadList: false,
    beforeUpload: handleZoneMappingUpload,
    disabled: uploading
  };

  return (
    <Modal
      title="导入Zone映射数据"
      open={visible}
      onCancel={handleCancel}
      width={800}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          取消
        </Button>,
        <Button
          key="import"
          type="primary"
          loading={uploading}
          onClick={handleImport}
          disabled={!pickupGateFileObjectKey || !zoneMappingFileObjectKey || availableHubs.length === 0}
        >
          开始导入
        </Button>
      ]}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label="1. 上传始发地Gate映射文件（Pickup Zip Code）"
          required
        >
          <Dragger {...pickupGateUploadProps}>
            <p className="ant-upload-drag-icon">
              <UploadOutlined />
            </p>
            <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
            <p className="ant-upload-hint">支持 .xlsx, .xls 格式</p>
          </Dragger>
          {pickupGateFile && (
            <div style={{ marginTop: 8 }}>
              <CheckOutlined style={{ color: '#52c41a' }} /> {pickupGateFile.name}
            </div>
          )}
        </Form.Item>

        <Form.Item
          label="2. 上传Zone映射文件（Delivery Zip Code）"
          required
        >
          <Dragger {...zoneMappingUploadProps}>
            <p className="ant-upload-drag-icon">
              <UploadOutlined />
            </p>
            <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
            <p className="ant-upload-hint">支持 .xlsx, .xls 格式</p>
          </Dragger>
          {zoneMappingFile && (
            <div style={{ marginTop: 8 }}>
              <CheckOutlined style={{ color: '#52c41a' }} /> {zoneMappingFile.name}
              {loadingHubs && <Spin size="small" style={{ marginLeft: 8 }} />}
            </div>
          )}
        </Form.Item>

        {availableHubs.length > 0 && (
          <Form.Item
            label="3. 选择要处理的HUB"
            name="selectedHubs"
            rules={[{ required: true, message: '请至少选择一个HUB' }]}
          >
            <Checkbox.Group>
              <Space direction="vertical">
                {availableHubs.map(hub => (
                  <Checkbox key={hub} value={hub}>
                    {hub}
                  </Checkbox>
                ))}
              </Space>
            </Checkbox.Group>
          </Form.Item>
        )}

        <Form.Item
          label="4. Gateway映射（可选）"
          name="gatewayMapping"
          tooltip="如果Gateway列名和HUB名不同，请设置映射关系。格式：每行一个映射，HUB:Gateway，例如：LAX:LAX_Gateway"
        >
          <TextArea
            rows={4}
            placeholder="例如：&#10;LAX:LAX_Gateway&#10;DFW:DFW_Gateway"
          />
        </Form.Item>

        <Form.Item
          label="5. 导入原因（可选）"
          name="reason"
        >
          <TextArea
            rows={2}
            placeholder="请输入导入原因，便于后续追踪"
          />
        </Form.Item>

        {importResult && (
          <Form.Item>
            <Alert
              message={`导入结果：成功 ${importResult.successCount} 条，失败 ${importResult.failedCount} 条`}
              type={importResult.success ? 'success' : 'error'}
              description={importResult.message}
              showIcon
            />
            {importResult.changeSummary && (
              <div style={{ marginTop: 12 }}>
                <div>数据变更摘要：</div>
                <ul style={{ paddingLeft: 20, marginTop: 4 }}>
                  <li>新增区间：{importResult.changeSummary.insertedCount}</li>
                  <li>调整区间：{importResult.changeSummary.updatedCount}</li>
                  <li>删除区间：{importResult.changeSummary.deletedCount}</li>
                  <li>跳过（已存在相同配置）：{importResult.changeSummary.skippedCount}</li>
                </ul>
                {importResult.changeLogDownloadUrl && (
                  <Button
                    type="link"
                    href={importResult.changeLogDownloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ paddingLeft: 0 }}
                  >
                    下载数据变更说明
                  </Button>
                )}
              </div>
            )}
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

export default ZoneMappingImportModal;

